import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Calendar,
  Check,
  ChevronRight,
  Loader2,
  MapPin,
  Plus,
  Settings2,
  Trash2,
  UserCog,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  usersAPI,
  scheduleBlackoutsAPI,
  schedulingSettingsAPI,
} from '@shared/apiClient';

const CAPABILITIES = [
  { id: 'inspection', label: 'Inspections' },
  { id: 'service', label: 'Service' },
];

const COOLING_CAPABILITIES = [
  { id: 'air', label: 'Air-cooled' },
  { id: 'water', label: 'Water-cooled' },
];

const emptyProfile = {
  capabilities: ['inspection'],
  coolingCapabilities: ['air', 'water'],
  includeInAutoSchedule: false,
  dailyCapacityMinutes: '',
  homeAddress: '',
  startFromHomeAddress: false,
};

function TechProfileForm({ tech, defaultCapacity, onSaved }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyProfile);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm({
      capabilities: tech.capabilities?.length ? tech.capabilities : ['inspection'],
      coolingCapabilities: tech.coolingCapabilities?.length ? tech.coolingCapabilities : ['air', 'water'],
      includeInAutoSchedule: tech.includeInAutoSchedule ?? false,
      dailyCapacityMinutes: tech.dailyCapacityMinutes ?? '',
      homeAddress: tech.homeAddress ?? '',
      startFromHomeAddress: tech.startFromHomeAddress ?? false,
    });
    setDirty(false);
  }, [
    tech.id,
    tech.capabilities,
    tech.coolingCapabilities,
    tech.includeInAutoSchedule,
    tech.dailyCapacityMinutes,
    tech.homeAddress,
    tech.startFromHomeAddress,
  ]);

  const saveMutation = useMutation(
    (data) => usersAPI.updateUser(tech.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['users', 'technicians']);
        queryClient.invalidateQueries('users');
        setDirty(false);
        toast.success(`${tech.firstName} ${tech.lastName} updated`);
        onSaved?.();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to save');
      },
    }
  );

  const toggleCapability = (cap) => {
    setForm((prev) => {
      const caps = prev.capabilities.includes(cap)
        ? prev.capabilities.filter((c) => c !== cap)
        : [...prev.capabilities, cap];
      return { ...prev, capabilities: caps.length ? caps : ['inspection'] };
    });
    setDirty(true);
  };

  // A tech with no cooling type selected could never be scheduled, so the last
  // one can't be turned off — unchecking it falls back to the other.
  const toggleCoolingCapability = (cap) => {
    setForm((prev) => {
      const caps = prev.coolingCapabilities.includes(cap)
        ? prev.coolingCapabilities.filter((c) => c !== cap)
        : [...prev.coolingCapabilities, cap];
      return {
        ...prev,
        coolingCapabilities: caps.length
          ? caps
          : [COOLING_CAPABILITIES.find((c) => c.id !== cap).id],
      };
    });
    setDirty(true);
  };

  const handleSave = () => {
    saveMutation.mutate({
      capabilities: form.capabilities,
      coolingCapabilities: form.coolingCapabilities,
      includeInAutoSchedule: form.includeInAutoSchedule,
      dailyCapacityMinutes: form.dailyCapacityMinutes === '' || form.dailyCapacityMinutes == null
        ? null
        : Number(form.dailyCapacityMinutes),
      homeAddress: form.homeAddress.trim() || null,
      startFromHomeAddress: form.startFromHomeAddress,
    });
  };

  const homeGeocoded = tech.homeLatitude != null && tech.homeLongitude != null;
  const hasHomeAddressInput = Boolean(form.homeAddress.trim());

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Capabilities</h3>
        <div className="flex flex-wrap gap-3">
          {CAPABILITIES.map(({ id, label }) => (
            <label key={id} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.capabilities.includes(id)}
                onChange={() => toggleCapability(id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {label}
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">Newer techs often start with inspections only.</p>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Generator cooling types</h3>
        <div className="flex flex-wrap gap-3">
          {COOLING_CAPABILITIES.map(({ id, label }) => (
            <label key={id} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.coolingCapabilities.includes(id)}
                onChange={() => toggleCoolingCapability(id)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {label}
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Clients whose cooling type isn&apos;t recorded yet can go to anyone.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.includeInAutoSchedule}
            onChange={(e) => {
              setForm({ ...form, includeInAutoSchedule: e.target.checked });
              setDirty(true);
            }}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Include in auto-schedule runs
        </label>

        <div className="flex-1 max-w-xs">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Daily work budget (minutes)
          </label>
          <input
            type="number"
            min="30"
            max="1440"
            step="15"
            value={form.dailyCapacityMinutes}
            onChange={(e) => {
              setForm({ ...form, dailyCapacityMinutes: e.target.value });
              setDirty(true);
            }}
            placeholder={`Default: ${defaultCapacity}`}
            className="input w-full text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1 inline-flex items-center gap-1.5">
          <MapPin className="h-4 w-4 text-gray-500" />
          Home address
        </label>
        <input
          type="text"
          value={form.homeAddress}
          onChange={(e) => {
            setForm({ ...form, homeAddress: e.target.value });
            setDirty(true);
          }}
          placeholder="456 Oak Ave, Jackson, MS"
          className="input w-full text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">
          {homeGeocoded && !dirty
            ? `Geocoded ✓ (${Number(tech.homeLatitude).toFixed(4)}, ${Number(tech.homeLongitude).toFixed(4)}).`
            : 'Saved addresses are geocoded automatically when you save.'}
        </p>
        <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.startFromHomeAddress}
            disabled={!hasHomeAddressInput}
            onChange={(e) => {
              setForm({ ...form, startFromHomeAddress: e.target.checked });
              setDirty(true);
            }}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
          />
          Start route from home address
        </label>
        <p className="text-xs text-gray-500 mt-1">
          When enabled and geocoded, auto-schedule and daily routes start from this address instead of the shop.
        </p>
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={!dirty || saveMutation.isLoading}
        className="btn btn-primary inline-flex items-center gap-2 text-sm"
      >
        {saveMutation.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Check className="h-4 w-4" />
        )}
        Save profile
      </button>
    </div>
  );
}

function TechBlackoutsPanel({ techId }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ startDate: '', endDate: '', reason: '' });

  const { data, isLoading } = useQuery(
    ['scheduleBlackouts', techId],
    () => scheduleBlackoutsAPI.getAll({ technicianId: techId }),
    { enabled: !!techId }
  );

  const blackouts = data?.data?.blackouts || [];

  const createMutation = useMutation(
    (payload) => scheduleBlackoutsAPI.create(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['scheduleBlackouts', techId]);
        queryClient.invalidateQueries('scheduleBlackouts');
        setForm({ startDate: '', endDate: '', reason: '' });
        toast.success('Time off added');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to add time off');
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => scheduleBlackoutsAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['scheduleBlackouts', techId]);
        queryClient.invalidateQueries('scheduleBlackouts');
        toast.success('Time off removed');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove time off');
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.startDate || !form.endDate) {
      toast.error('Start and end dates are required');
      return;
    }
    createMutation.mutate({
      technicianId: techId,
      startDate: form.startDate,
      endDate: form.endDate,
      reason: form.reason || undefined,
    });
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <input
          type="date"
          value={form.startDate}
          onChange={(e) => setForm({ ...form, startDate: e.target.value })}
          className="input text-sm"
          required
        />
        <input
          type="date"
          value={form.endDate}
          onChange={(e) => setForm({ ...form, endDate: e.target.value })}
          className="input text-sm"
          required
        />
        <input
          type="text"
          placeholder="Reason (optional)"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          className="input text-sm"
        />
        <button
          type="submit"
          disabled={createMutation.isLoading}
          className="btn btn-secondary inline-flex items-center justify-center gap-1 text-sm"
        >
          <Plus className="h-4 w-4" />
          Add time off
        </button>
      </form>

      {isLoading ? (
        <div className="text-sm text-gray-500">Loading time off...</div>
      ) : blackouts.length === 0 ? (
        <div className="text-sm text-gray-500">No scheduled time off for this technician.</div>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md overflow-hidden">
          {blackouts.map((b) => (
            <li key={b.id} className="flex items-center justify-between px-3 py-2 text-sm bg-white">
              <div>
                <span className="font-medium text-gray-900">{b.startDate} — {b.endDate}</span>
                {b.reason && <span className="text-gray-500 ml-2">({b.reason})</span>}
              </div>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(b.id)}
                className="text-red-600 hover:text-red-800 p-1"
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function TechScheduling() {
  const [selectedId, setSelectedId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const { data: techsData, isLoading: techsLoading } = useQuery(
    ['users', 'technicians', 'scheduling'],
    () => usersAPI.getUsers({ role: 'technician', limit: 100 })
  );

  const { data: settingsData } = useQuery(
    'schedulingSettings',
    () => schedulingSettingsAPI.get()
  );

  const allTechs = techsData?.data?.users || [];
  const technicians = useMemo(
    () => (showInactive ? allTechs : allTechs.filter((t) => t.isActive)),
    [allTechs, showInactive]
  );

  const defaultCapacity = settingsData?.data?.settings?.defaultDailyWorkMinutes ?? 480;
  const selectedTech = technicians.find((t) => t.id === selectedId) || technicians[0] || null;

  useEffect(() => {
    if (!selectedId && technicians.length > 0) {
      setSelectedId(technicians[0].id);
    }
    if (selectedId && !technicians.find((t) => t.id === selectedId) && technicians.length > 0) {
      setSelectedId(technicians[0].id);
    }
  }, [selectedId, technicians]);

  const capabilitySummary = (tech) => {
    const caps = tech.capabilities || [];
    let summary;
    if (caps.includes('service') && caps.includes('inspection')) summary = 'Service + Inspection';
    else if (caps.includes('service')) summary = 'Service only';
    else summary = 'Inspection only';

    // Only worth calling out when the tech is limited to one cooling type;
    // "both" is the default and would be noise on every row.
    const cooling = tech.coolingCapabilities || ['air', 'water'];
    if (cooling.length === 1) {
      summary += cooling[0] === 'water' ? ' · Water-cooled only' : ' · Air-cooled only';
    }
    return summary;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="hub-page-header">
        <h1>Technician Scheduling</h1>
        <p>
          Manage what each tech can do, auto-schedule participation, daily capacity, and time off.
          {' '}
          <Link to="/settings" className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
            <Settings2 className="h-3.5 w-3.5" />
            Global scheduling rules
          </Link>
        </p>
      </div>

      <div className="flex-1 overflow-hidden p-4">
        {techsLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Loading technicians...
          </div>
        ) : technicians.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            No technicians found. Add users with the Technician role first.
          </div>
        ) : (
          <div className="h-full flex flex-col lg:flex-row gap-4 min-h-0">
            <div className="lg:w-72 flex-shrink-0 flex flex-col min-h-0 bg-white rounded-lg border border-surface-border shadow-panel">
              <div className="p-3 border-b border-surface-border flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Technicians</span>
                <label className="flex items-center gap-1.5 text-2xs text-gray-500">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Show inactive
                </label>
              </div>
              <ul className="flex-1 overflow-auto divide-y divide-gray-100">
                {technicians.map((tech) => {
                  const selected = tech.id === selectedTech?.id;
                  return (
                    <li key={tech.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(tech.id)}
                        className={clsx(
                          'w-full text-left px-3 py-2.5 flex items-center gap-2 transition-colors',
                          selected ? 'bg-primary-50 border-l-2 border-l-primary-600' : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                        )}
                      >
                        <UserCog className={clsx('h-4 w-4 flex-shrink-0', selected ? 'text-primary-600' : 'text-gray-400')} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {tech.firstName} {tech.lastName}
                          </div>
                          <div className="text-2xs text-gray-500 truncate">
                            {capabilitySummary(tech)}
                            {!tech.isActive && ' · Inactive'}
                            {!tech.includeInAutoSchedule && ' · Manual only'}
                          </div>
                        </div>
                        <ChevronRight className={clsx('h-4 w-4 flex-shrink-0', selected ? 'text-primary-500' : 'text-gray-300')} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {selectedTech && (
              <div className="flex-1 min-w-0 overflow-auto space-y-4">
                <div className="bg-white rounded-lg border border-surface-border shadow-panel p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {selectedTech.firstName} {selectedTech.lastName}
                      </h2>
                      <p className="text-sm text-gray-500">@{selectedTech.username}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      <span className={clsx(
                        'px-2 py-0.5 rounded-full text-2xs font-medium',
                        selectedTech.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      )}>
                        {selectedTech.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <span className={clsx(
                        'px-2 py-0.5 rounded-full text-2xs font-medium',
                        selectedTech.includeInAutoSchedule ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                      )}>
                        {selectedTech.includeInAutoSchedule ? 'Auto-schedule on' : 'Auto-schedule off'}
                      </span>
                    </div>
                  </div>
                  <TechProfileForm tech={selectedTech} defaultCapacity={defaultCapacity} />
                </div>

                <div className="bg-white rounded-lg border border-surface-border shadow-panel p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="h-5 w-5 text-gray-600" />
                    <h2 className="text-base font-semibold text-gray-900">Time off & blackouts</h2>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Dates this technician is unavailable for auto-scheduling (vacation, sick leave, etc.).
                    Company-wide holidays are managed under Settings → Scheduling.
                  </p>
                  <TechBlackoutsPanel techId={selectedTech.id} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
